import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { Flame, Star, Play, Users } from "lucide-react";

/**
 * Two side-by-side rails on the Portal:
 *   - Most watched: auto-derived from user_watch_progress.
 *   - Editor's picks: admin-curated via `is_editors_pick`.
 *
 * Each rail self-hides if its source has no items. The full block hides if both
 * rails are empty (so brand-new users don't see two empty boxes).
 *
 * Clicking a card calls `onOpenTalk(session)` which delegates to the same
 * TalkModal used by the schedule (auto-marks in_progress + lets user mark
 * watched).
 */
export default function DiscoveryRails({ onOpenTalk }) {
  const [mostWatched, setMostWatched] = useState([]);
  const [editorsPicks, setEditorsPicks] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [mw, ep] = await Promise.all([
          api.get("/conference/most-watched?limit=6"),
          api.get("/conference/editors-picks"),
        ]);
        if (!alive) return;
        setMostWatched(mw.data?.items || []);
        setEditorsPicks(ep.data?.items || []);
      } catch {
        if (alive) {
          setMostWatched([]);
          setEditorsPicks([]);
        }
      } finally {
        if (alive) setLoaded(true);
      }
    })();
    return () => { alive = false; };
  }, []);

  if (!loaded) return null;
  if (mostWatched.length === 0 && editorsPicks.length === 0) return null;

  return (
    <section
      id="discover"
      className="grid grid-cols-1 lg:grid-cols-2 gap-6"
      data-testid="discovery-rails"
    >
      {editorsPicks.length > 0 && (
        <Rail
          eyebrow="Editor's picks"
          title="Hand-picked talks"
          icon={Star}
          iconColor="#F5B400"
          items={editorsPicks}
          onOpenTalk={onOpenTalk}
          testId="rail-editors"
          chipLabel="Pick"
          chipBg="bg-amber-50 text-amber-700 ring-amber-200"
        />
      )}
      {mostWatched.length > 0 && (
        <Rail
          eyebrow="Trending"
          title="Most watched"
          icon={Flame}
          iconColor="#DC4F48"
          items={mostWatched}
          onOpenTalk={onOpenTalk}
          testId="rail-most-watched"
          showViewers
        />
      )}
    </section>
  );
}

function Rail({ eyebrow, title, icon: Icon, iconColor, items, onOpenTalk, testId, chipLabel, chipBg, showViewers }) {
  return (
    <div
      className="rounded-3xl bg-white ring-1 ring-black/5 p-6 sm:p-8"
      data-testid={testId}
    >
      <div className="flex items-start gap-3">
        <div
          className="h-11 w-11 rounded-2xl flex items-center justify-center shrink-0"
          style={{ backgroundColor: `${iconColor}1A`, color: iconColor, boxShadow: `inset 0 0 0 1px ${iconColor}33` }}
        >
          <Icon size={18} strokeWidth={1.8} />
        </div>
        <div className="min-w-0">
          <div className="text-xs uppercase tracking-[0.22em] text-slate-500 font-semibold">{eyebrow}</div>
          <h3 className="mt-1 font-heading text-xl text-slate-900 font-medium tracking-tight">{title}</h3>
        </div>
      </div>

      <ul className="mt-5 divide-y divide-slate-100">
        {items.slice(0, 5).map((s, idx) => (
          <li key={s.session_id} className="py-3 first:pt-0 last:pb-0">
            <button
              type="button"
              onClick={() => onOpenTalk?.(s)}
              disabled={!s.video_url}
              className="w-full flex items-start gap-3 text-left rounded-xl px-2 -mx-2 py-1 hover:bg-slate-50 disabled:cursor-default disabled:hover:bg-transparent transition-colors group"
              data-testid={`${testId}-item-${s.session_id}`}
            >
              <span className="font-mono text-xs text-slate-400 w-5 pt-0.5 shrink-0 text-right">{idx + 1}</span>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium text-slate-900 leading-snug line-clamp-2">{s.title || "TBC"}</div>
                <div className="mt-0.5 text-xs text-slate-500 flex items-center gap-2 flex-wrap">
                  {s.speaker && <span className="truncate">{s.speaker}</span>}
                  {chipLabel && (
                    <span className={`inline-flex items-center rounded-full ring-1 text-[10px] font-semibold uppercase tracking-[0.14em] px-1.5 py-0.5 ${chipBg}`}>
                      {chipLabel}
                    </span>
                  )}
                  {showViewers && typeof s.viewers === "number" && (
                    <span className="inline-flex items-center gap-1 text-slate-500">
                      <Users size={10} /> {s.viewers}
                    </span>
                  )}
                </div>
              </div>
              {s.video_url ? (
                <span className="opacity-0 group-hover:opacity-100 transition-opacity inline-flex items-center justify-center h-7 w-7 rounded-full bg-[#28BBE3] text-white shrink-0">
                  <Play size={11} strokeWidth={2.6} />
                </span>
              ) : (
                <span className="text-[10px] uppercase tracking-[0.14em] text-slate-400 font-semibold shrink-0">Coming soon</span>
              )}
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}
